"use client";

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Service {
  title: string;
  description: string;
  backgroundImage: string;
}

interface ServicesAccordionProps {
  title: string;
  services: Service[];
}

const ServicesAccordion: React.FC<ServicesAccordionProps> = ({ title, services }) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    setExpandedIndex(isMobile ? 0 : 0);
  }, [isMobile]);

  return (
    <section className="my-24 w-full">
      <h2 className="text-3xl font-bold text-center mb-12 px-4 sm:px-6 lg:px-8">{title}</h2>

      {/* Desktop-версия */}
      {!isMobile && (
        <div className="flex justify-center h-[600px] w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="relative overflow-hidden cursor-pointer mx-2"
              initial={{ width: '15%' }}
              animate={{ width: expandedIndex === index ? '55%' : '15%' }}
              transition={{ duration: 0.8, ease: [0.04, 0.62, 0.23, 0.98] }}
              onHoverStart={() => setExpandedIndex(index)}
              style={{
                backgroundImage: `url(${service.backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <div className="absolute inset-0 bg-black/50">
                <motion.div
                  className="absolute bottom-8 left-8 origin-bottom-left"
                  initial={{ opacity: 1 }}
                  animate={{ opacity: expandedIndex === index ? 0 : 1 }}
                  transition={{ duration: 0.6, ease: 'easeInOut' }}
                >
                  <h3
                    className="text-white font-bold text-2xl whitespace-nowrap"
                    style={{ transform: 'rotate(-90deg)', transformOrigin: 'bottom left' }}
                  >
                    {service.title}
                  </h3>
                </motion.div>
                <AnimatePresence>
                  {expandedIndex === index && (
                    <motion.div
                      className="p-8 text-white h-full flex flex-col justify-end"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1, transition: { duration: 0.5, delay: 0.3 } }}
                      exit={{ opacity: 0, transition: { duration: 0.3 } }}
                    >
                      <h3 className="text-3xl font-bold mb-4">{service.title}</h3>
                      <p className="text-white/80 max-w-md">{service.description}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Mobile-версия */}
      {isMobile && (
        <div className="px-4 sm:px-6 space-y-4">
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="relative w-full overflow-hidden cursor-pointer"
              onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
              initial={false}
              animate={{ height: expandedIndex === index ? 300 : 100 }}
              transition={{ duration: 0.8, ease: [0.04, 0.62, 0.23, 0.98] }}
              style={{
                backgroundImage: `url(${service.backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <div className="absolute inset-0 bg-black/60"></div>
              <div className="relative p-4 h-full flex flex-col justify-center text-white">
                <h3 className="font-bold text-lg mb-2">{service.title}</h3>
                <AnimatePresence>
                  {expandedIndex === index && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      className="text-white/80 text-sm"
                    >
                      {service.description}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ServicesAccordion;
